import {
  ElementPickerController,
  ElementPickerOptions,
  ElementPickerPayload,
  HighlightElementOptions,
  TrackedMarkerItem,
  clearTrackedMarkers,
  highlightElementByXpath,
  initElementPicker,
  syncTrackedMarkers,
} from './picker';
import { logger } from './logger';

export const PICKER_MESSAGE_SOURCE = {
  PLATFORM: 'visual-tracking-platform',
  TARGET: 'visual-tracking-target',
} as const;

export const PICKER_MESSAGE_TYPE = {
  ENABLE_PICKER: 'ENABLE_PICKER',
  DISABLE_PICKER: 'DISABLE_PICKER',
  HIGHLIGHT_ELEMENT: 'HIGHLIGHT_ELEMENT',
  SYNC_TRACKED_MARKERS: 'SYNC_TRACKED_MARKERS',
  ELEMENT_SELECTED: 'ELEMENT_SELECTED',
  PICKER_READY: 'PICKER_READY',
  PICKER_ERROR: 'PICKER_ERROR',
} as const;

export type PlatformPickerMessageType =
  | typeof PICKER_MESSAGE_TYPE.ENABLE_PICKER
  | typeof PICKER_MESSAGE_TYPE.DISABLE_PICKER
  | typeof PICKER_MESSAGE_TYPE.HIGHLIGHT_ELEMENT
  | typeof PICKER_MESSAGE_TYPE.SYNC_TRACKED_MARKERS;

export type TargetPickerMessageType =
  | typeof PICKER_MESSAGE_TYPE.ELEMENT_SELECTED
  | typeof PICKER_MESSAGE_TYPE.PICKER_READY
  | typeof PICKER_MESSAGE_TYPE.PICKER_ERROR;

export interface PickerBridgeMessage<TPayload = unknown> {
  source: string;
  type: string;
  requestId: string;
  payload?: TPayload;
}

export interface PickerBridgeOptions {
  win?: Window;
  root?: Document;
  allowedOrigins?: string[];
  targetOrigin?: string;
  createPicker?: (options: ElementPickerOptions) => ElementPickerController;
  highlightElement?: (options: HighlightElementOptions) => boolean;
}

export interface PickerBridgeController {
  destroy: () => void;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object');
}

function isPlatformPickerMessage(value: unknown): value is PickerBridgeMessage {
  if (!isObject(value)) {
    return false;
  }

  if (value.source !== PICKER_MESSAGE_SOURCE.PLATFORM) {
    return false;
  }

  if (
    value.type !== PICKER_MESSAGE_TYPE.ENABLE_PICKER &&
    value.type !== PICKER_MESSAGE_TYPE.DISABLE_PICKER &&
    value.type !== PICKER_MESSAGE_TYPE.HIGHLIGHT_ELEMENT &&
    value.type !== PICKER_MESSAGE_TYPE.SYNC_TRACKED_MARKERS
  ) {
    return false;
  }

  return typeof value.requestId === 'string';
}

function getHighlightXpath(message: PickerBridgeMessage): string {
  if (!isObject(message.payload)) {
    return '';
  }

  const xpath = message.payload.xpath;
  return typeof xpath === 'string' ? xpath.trim() : '';
}

function getTrackedMarkers(message: PickerBridgeMessage): TrackedMarkerItem[] {
  if (!isObject(message.payload) || !Array.isArray(message.payload.markers)) {
    return [];
  }

  const markers: TrackedMarkerItem[] = [];
  message.payload.markers.forEach((item) => {
    if (!isObject(item)) {
      return;
    }
    const xpath = typeof item.xpath === 'string' ? item.xpath.trim() : '';
    const status = item.status;
    if (!xpath || (status !== 'tracked' && status !== 'invalid')) {
      return;
    }
    markers.push({
      xpath,
      status,
      tagName: typeof item.tagName === 'string' ? item.tagName : undefined,
      text: typeof item.text === 'string' ? item.text : undefined,
    });
  });
  return markers;
}

function isAllowedOrigin(origin: string, allowedOrigins?: string[]): boolean {
  if (!allowedOrigins || allowedOrigins.length === 0) {
    return true;
  }

  return allowedOrigins.indexOf(origin) !== -1;
}

function resolveTargetOrigin(
  eventOrigin: string,
  fixedTargetOrigin?: string
): string {
  if (typeof fixedTargetOrigin === 'string' && fixedTargetOrigin.trim()) {
    return fixedTargetOrigin.trim();
  }

  return eventOrigin || '*';
}

function postToParent(
  win: Window,
  message: PickerBridgeMessage,
  targetOrigin: string
): void {
  if (!win.parent || typeof win.parent.postMessage !== 'function') {
    logger.warn('圈选桥接消息发送失败：父窗口不可用');
    return;
  }

  win.parent.postMessage(message, targetOrigin);
}

function createTargetMessage<TPayload>(
  type: TargetPickerMessageType,
  requestId: string,
  payload?: TPayload
): PickerBridgeMessage<TPayload> {
  return {
    source: PICKER_MESSAGE_SOURCE.TARGET,
    type,
    requestId,
    payload,
  };
}

function createEmptyBridgeController(): PickerBridgeController {
  return {
    destroy() {
      return undefined;
    },
  };
}

export function initPickerMessageBridge(
  options: PickerBridgeOptions = {}
): PickerBridgeController {
  const win =
    options.win || (typeof window === 'undefined' ? undefined : window);

  if (!win || typeof win.addEventListener !== 'function') {
    logger.error('圈选桥接初始化失败：缺少有效 window');
    return createEmptyBridgeController();
  }

  const bridgeWindow = win;
  const createPicker = options.createPicker || initElementPicker;
  const highlightElement = options.highlightElement || highlightElementByXpath;
  let currentPicker: ElementPickerController | null = null;
  let latestRequestId = '';
  let latestTargetOrigin = options.targetOrigin || '*';

  function destroyCurrentPicker(): void {
    if (!currentPicker) {
      return;
    }

    try {
      currentPicker.destroy();
    } catch (error) {
      logger.error('圈选桥接关闭圈选器异常', error);
    } finally {
      currentPicker = null;
    }
  }

  function postMessage<TPayload>(
    type: TargetPickerMessageType,
    requestId: string,
    payload?: TPayload
  ): void {
    postToParent(
      bridgeWindow,
      createTargetMessage(type, requestId, payload),
      latestTargetOrigin
    );
  }

  function handlePickerSelected(payload: ElementPickerPayload): void {
    postMessage(PICKER_MESSAGE_TYPE.ELEMENT_SELECTED, latestRequestId, payload);
  }

  function handleMessage(event: MessageEvent): void {
    try {
      if (!isAllowedOrigin(event.origin, options.allowedOrigins)) {
        logger.warn('圈选桥接忽略非白名单来源消息：' + event.origin);
        return;
      }

      if (!isPlatformPickerMessage(event.data)) {
        return;
      }

      latestRequestId = event.data.requestId;
      latestTargetOrigin = resolveTargetOrigin(
        event.origin,
        options.targetOrigin
      );

      if (event.data.type === PICKER_MESSAGE_TYPE.DISABLE_PICKER) {
        destroyCurrentPicker();
        return;
      }

      if (event.data.type === PICKER_MESSAGE_TYPE.HIGHLIGHT_ELEMENT) {
        const xpath = getHighlightXpath(event.data);

        if (!xpath) {
          postMessage(PICKER_MESSAGE_TYPE.PICKER_ERROR, latestRequestId, {
            message: '反向高亮失败：XPath 不能为空',
          });
          return;
        }

        const highlighted = highlightElement({
          root: options.root,
          xpath,
        });

        if (!highlighted) {
          postMessage(PICKER_MESSAGE_TYPE.PICKER_ERROR, latestRequestId, {
            message: '反向高亮失败：未找到目标元素',
          });
        }

        return;
      }

      if (event.data.type === PICKER_MESSAGE_TYPE.SYNC_TRACKED_MARKERS) {
        syncTrackedMarkers({
          root: options.root,
          markers: getTrackedMarkers(event.data),
        });
        return;
      }

      destroyCurrentPicker();
      currentPicker = createPicker({
        root: options.root,
        onSelect: handlePickerSelected,
      });
    } catch (error) {
      logger.error('圈选桥接处理消息异常', error);
      postMessage(PICKER_MESSAGE_TYPE.PICKER_ERROR, latestRequestId, {
        message: '圈选桥接处理消息异常',
      });
    }
  }

  function announceReady() {
    postMessage(PICKER_MESSAGE_TYPE.PICKER_READY, '', {
      version: 'ehr-web',
    });
  }

  bridgeWindow.addEventListener('message', handleMessage);
  // 立即 + 延迟再发一次，避免父页 iframe onLoad / 监听器注册时序导致漏收
  announceReady();
  const readyTimer = bridgeWindow.setTimeout(() => {
    announceReady();
  }, 300);

  return {
    destroy() {
      bridgeWindow.clearTimeout(readyTimer);
      destroyCurrentPicker();
      clearTrackedMarkers(options.root);
      bridgeWindow.removeEventListener('message', handleMessage);
    },
  };
}
